import React, { useState } from 'react';
import MailGestaltungPanel from '../components/MailGestaltungPanel';
import { Blaetterleiste, TabellenKopf, useTabelle, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, Lock, Unlock, Pencil, X, Mail } from 'lucide-react';
import { customers } from '../api';
import StatusBadge from '../components/StatusBadge';
import CustomerUsersPanel from '../components/CustomerUsersPanel';
import MitarbeiterPanel from '../components/MitarbeiterPanel';
import AnbieterZugaengePanel from '../components/AnbieterZugaengePanel';
import LizenzenPanel from '../components/LizenzenPanel';
import type { Customer, CustomerStatus } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { useEinstellung } from '../contexts/EinstellungenContext';

function CustomerRow({
  customer,
  onEdit,
  onSuspend,
  onReactivate,
  onMailGestaltung,
}: {
  customer: Customer;
  onEdit: (c: Customer) => void;
  onSuspend: (c: Customer) => void;
  onReactivate: (c: Customer) => void;
  onMailGestaltung: (c: Customer) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { data: customerOrders, isLoading: loadingOrders } = useQuery({
    queryKey: ['customers', customer.id, 'orders'],
    queryFn: () => customers.getOrders(customer.id),
    enabled: open,
  });

  const { data: customerInstances, isLoading: loadingInstances } = useQuery({
    queryKey: ['customers', customer.id, 'instances'],
    queryFn: () => customers.getInstances(customer.id),
    enabled: open,
  });

  return (
    <>
      <tr
        className="hover:bg-slate-50 cursor-pointer"
        onClick={() => setOpen((o) => !o)}
      >
        <td className="px-4 py-3 font-medium text-slate-800">{customer.companyName}</td>
        <td className="px-4 py-3 text-slate-600 text-xs">{customer.billingEmail}</td>
        <td className="px-4 py-3">
          <StatusBadge status={customer.status} type="customer" />
        </td>
        <td className="px-4 py-3 text-slate-500 text-xs text-center">
          {customer._count?.instances ?? '—'}
        </td>
        <td className="px-4 py-3 text-slate-500 text-xs text-center">
          {customer._count?.orders ?? '—'}
        </td>
        <td className="px-4 py-3 text-slate-500 text-xs">
          {formatDistanceToNow(new Date(customer.createdAt), { addSuffix: true })}
        </td>
        <td className="px-4 py-3 text-slate-400">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="bg-slate-50 px-6 py-4 border-t border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic info */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('customers.accountInfo')}</h4>
                <dl className="space-y-1 text-sm">
                  <div className="flex gap-2">
                    <dt className="text-slate-500 w-32 shrink-0">ID</dt>
                    <dd className="font-mono text-xs text-slate-700">{customer.id}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-slate-500 w-32 shrink-0">{t('customers.billingEmail')}</dt>
                    <dd className="text-slate-700">{customer.billingEmail}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-slate-500 w-32 shrink-0">{t('customers.technicalEmail')}</dt>
                    <dd className="text-slate-700">{customer.technicalEmail ?? '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-slate-500 w-32 shrink-0">{t('customers.wcCustomerId')}</dt>
                    <dd className="text-slate-700">{customer.woocommerceCustomerId ?? '—'}</dd>
                  </div>
                </dl>

                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4 mb-2">
                  Rechnungsdaten
                </h4>
                {!customer.billingAddressLine1 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    Unvollständig — ohne Anschrift lässt sich keine Rechnung stellen.
                  </p>
                ) : (
                  <dl className="space-y-1 text-sm">
                    <div className="flex gap-2">
                      <dt className="text-slate-500 w-32 shrink-0">Anschrift</dt>
                      <dd className="text-slate-700">
                        {customer.billingName || customer.companyName}
                        <br />
                        {customer.billingAddressLine1}
                        {customer.billingAddressLine2 && (
                          <>
                            <br />
                            {customer.billingAddressLine2}
                          </>
                        )}
                        <br />
                        {customer.billingPostalCode} {customer.billingCity}
                        {customer.billingCountry && customer.billingCountry !== 'DE' && (
                          <>
                            <br />
                            {customer.billingCountry}
                          </>
                        )}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-slate-500 w-32 shrink-0">USt-IdNr.</dt>
                      <dd className="text-slate-700 font-mono text-xs">
                        {customer.vatId ?? '—'}
                        {customer.vatId &&
                          customer.billingCountry &&
                          customer.billingCountry !== 'DE' && (
                            <span className="ml-2 text-amber-700 font-sans">{t('customers.reverseCharge')}</span>
                          )}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-slate-500 w-32 shrink-0">Zahlungsziel</dt>
                      <dd className="text-slate-700">{customer.paymentTermsDays} Tage</dd>
                    </div>
                    {customer.customerReference && (
                      <div className="flex gap-2">
                        <dt className="text-slate-500 w-32 shrink-0">Referenz</dt>
                        <dd className="text-slate-700">{customer.customerReference}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>

              {/* Orders */}
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Recent Orders
                </h4>
                {loadingOrders ? (
                  <div className="animate-pulse space-y-1">
                    {[1, 2].map((i) => <div key={i} className="h-5 bg-slate-200 rounded" />)}
                  </div>
                ) : !customerOrders?.length ? (
                  <p className="text-xs text-slate-400 italic">{t('customers.noOrders')}</p>
                ) : (
                  <ul className="space-y-1">
                    {customerOrders.slice(0, 5).map((o) => (
                      <li key={o.id} className="flex items-center gap-2 text-xs text-slate-600">
                        <StatusBadge status={o.status} type="order" />
                        <span>{o.type.replace(/_/g, ' ')}</span>
                        {o.woocommerceOrderId && <span className="text-slate-400">#{o.woocommerceOrderId}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Instances */}
              <div className="md:col-span-2">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Instances
                </h4>
                {loadingInstances ? (
                  <div className="animate-pulse space-y-1">
                    {[1, 2].map((i) => <div key={i} className="h-5 bg-slate-200 rounded" />)}
                  </div>
                ) : !customerInstances?.length ? (
                  <p className="text-xs text-slate-400 italic">{t('customers.noInstances')}</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {customerInstances.map((inst) => (
                      <div
                        key={inst.id}
                        className="bg-surface border border-slate-200 rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <StatusBadge status={inst.status} type="instance" />
                        </div>
                        <p className="text-xs font-mono text-slate-700 truncate">
                          {inst.domain ?? inst.subdomain ?? inst.id.slice(0, 12)}
                        </p>
                        <p className="text-xs text-slate-400">
                          {inst.product?.name ?? inst.productId.slice(0, 8)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <CustomerUsersPanel customer={customer} />
            </div>

            {/*
              * Direkt unter den Portalkonten, und mit einer Ueberschrift, die den
              * Unterschied sagt: oben die kaufmaennische Identitaet (Vertraege,
              * Rechnungen), hier die Arbeitszugaenge in den Anwendungen. Zwei
              * Abschnitte mit aehnlichem Namen nebeneinander waeren genau die
              * Verwechslung, die das Konzept trennen will.
              */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Mitarbeiter — Zugänge in den Anwendungen
              </h3>
              <MitarbeiterPanel customerId={customer.id} />
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <AnbieterZugaengePanel customer={customer} />
            </div>

            {/*
              * Und darunter die Zugaenge, die dem KUNDEN gehoeren. Die
              * Nachbarschaft ist Absicht: oben Server-Anbieter, hier
              * Paketserver — beides Zugaenge, die wir treuhaenderisch halten
              * und nicht besitzen.
              */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <LizenzenPanel customerId={customer.id} />
            </div>

            <div className="mt-4 pt-4 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => onEdit(customer)}
                className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
              >
                <Pencil size={12} /> Stammdaten bearbeiten
              </button>
              <button
                onClick={() => onMailGestaltung(customer)}
                className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
              >
                <Mail size={12} /> Mail-Gestaltung
              </button>
              {customer.status === 'ACTIVE' ? (
                <button
                  onClick={() => onSuspend(customer)}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
                >
                  <Lock size={12} /> Kunde sperren
                </button>
              ) : (
                <button
                  onClick={() => onReactivate(customer)}
                  className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"
                >
                  <Unlock size={12} /> Kunde entsperren
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useEinstellung<string>('kunden.suche', '');
  const [editing, setEditing] = useState<Customer | null>(null);
  // Eigenes Fenster statt eines Abschnitts in der Tabellenzeile: das Panel
  // bringt eine Vorschau mit und braucht Platz.
  const [gestaltungFuer, setGestaltungFuer] = useState<Customer | null>(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', { search }],
    queryFn: () => customers.list({ search: search || undefined }),
  });

  function done(msg: string) {
    return () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setError('');
      setNotice(msg);
    };
  }
  function fail(fallback: string) {
    return (err: unknown) => {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback,
      );
      setNotice('');
    };
  }

  const suspendMutation = useMutation({
    mutationFn: (c: Customer) => customers.suspend(c.id),
    onSuccess: done('Kunde gesperrt — alle Zugänge im Kundenbereich sind sofort blockiert.'),
    onError: fail('Sperren fehlgeschlagen.'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (c: Customer) => customers.reactivate(c.id),
    onSuccess: done('Kunde entsperrt.'),
    onError: fail('Entsperren fehlgeschlagen.'),
  });

  const rows = data ?? [];

  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    { key: 'firma', label: t('tables.customer'), wert: (c) => c.companyName },
    { key: 'email', label: t('customers.billingEmail'), wert: (c) => c.billingEmail ?? '' },
    { key: 'status', label: t('common.status'), wert: (c) => c.status },
    { key: 'instanzen', label: t('nav.instances'), wert: (c) => c._count?.instances ?? 0 },
    { key: 'bestellungen', label: t('nav.orders'), wert: (c) => c._count?.orders ?? 0 },
    { key: 'erstellt', label: t('common.createdAt'), wert: (c) => c.createdAt },
  ];

  const tabelle = useTabelle({
    speichern: 'kunden',
    zeilen: rows,
    spalten,
    startSortierung: { key: 'firma', richtung: 'auf' },
  });


  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder={t('customers.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-72"
        />
        <span className="ml-auto text-sm text-slate-500">{rows.length} Kunden</span>
        <button
          onClick={() => {
            setCreating(true);
            setError('');
          }}
          className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg"
        >
          <Plus size={15} /> Neuer Kunde
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
          {notice}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400 italic">
                  Keine Kunden gefunden.
                </td>
              </tr>
            ) : (
              tabelle.sichtbar.map((customer) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  onEdit={(c) => {
                    setEditing(c);
                    setError('');
                  }}
                  onSuspend={(c) => {
                    if (
                      confirm(
                        `Kunde "${c.companyName}" sperren? Alle Zugänge im Kundenbereich werden sofort blockiert. Laufende Instanzen bleiben unberührt.`,
                      )
                    ) {
                      suspendMutation.mutate(c);
                    }
                  }}
                  onReactivate={(c) => reactivateMutation.mutate(c)}
                  onMailGestaltung={(c) => setGestaltungFuer(c)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste blaettern={tabelle.blaettern} />
      {gestaltungFuer && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="w-full max-w-6xl rounded-lg bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">
                  Mail-Gestaltung — {gestaltungFuer.companyName}
                </h2>
                <p className="text-xs text-slate-500">
                  Gilt für alle Instanzen dieses Kunden, sofern eine Instanz nichts
                  Eigenes setzt.
                </p>
              </div>
              <button
                onClick={() => setGestaltungFuer(null)}
                className="text-sm text-slate-500 hover:text-slate-800"
              >
                Schließen
              </button>
            </div>
            <MailGestaltungPanel ziel={{ art: 'kunde', id: gestaltungFuer.id }} />
          </div>
        </div>
      )}

      {(creating || editing) && (
        <CustomerModal
          customer={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            const wasEditing = Boolean(editing);
            setCreating(false);
            setEditing(null);
            done(wasEditing ? 'Stammdaten gespeichert.' : 'Kunde angelegt.')();
          }}
        />
      )}
    </div>
  );
}

function CustomerModal({
  customer,
  onClose,
  onSaved,
}: {
  customer: Customer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    companyName: customer?.companyName ?? '',
    shortCode: customer?.shortCode ?? '',
    billingEmail: customer?.billingEmail ?? '',
    technicalEmail: customer?.technicalEmail ?? '',
    status: (customer?.status ?? 'ACTIVE') as CustomerStatus,
    billingName: customer?.billingName ?? '',
    billingAddressLine1: customer?.billingAddressLine1 ?? '',
    billingAddressLine2: customer?.billingAddressLine2 ?? '',
    billingPostalCode: customer?.billingPostalCode ?? '',
    billingCity: customer?.billingCity ?? '',
    billingCountry: customer?.billingCountry ?? 'DE',
    vatId: customer?.vatId ?? '',
    taxNumber: customer?.taxNumber ?? '',
    invoiceEmail: customer?.invoiceEmail ?? '',
    paymentTermsDays: String(customer?.paymentTermsDays ?? 14),
    customerReference: customer?.customerReference ?? '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        companyName: form.companyName,
        shortCode: form.shortCode.trim().toLowerCase(),
        billingEmail: form.billingEmail,
        technicalEmail: form.technicalEmail,
        status: form.status,
        billingName: form.billingName,
        billingAddressLine1: form.billingAddressLine1,
        billingAddressLine2: form.billingAddressLine2,
        billingPostalCode: form.billingPostalCode,
        billingCity: form.billingCity,
        billingCountry: form.billingCountry,
        vatId: form.vatId,
        taxNumber: form.taxNumber,
        invoiceEmail: form.invoiceEmail,
        paymentTermsDays: Number(form.paymentTermsDays) || 0,
        customerReference: form.customerReference,
      };
      return customer ? customers.update(customer.id, payload) : customers.create(payload);
    },
    onSuccess: onSaved,
    onError: (err: unknown) => {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Speichern fehlgeschlagen.',
      );
    },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-surface">
          <h2 className="text-base font-semibold text-slate-900">
            {customer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            mutation.mutate();
          }}
          className="px-6 py-5 space-y-4"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Firma</label>
            <input
              required
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Kurzform</label>
            <input
              value={form.shortCode}
              onChange={(e) => setForm((f) => ({ ...f, shortCode: e.target.value }))}
              placeholder="ancora"
              pattern="[a-zA-Z0-9]([a-zA-Z0-9-]{0,30}[a-zA-Z0-9])?"
              title="Buchstaben, Ziffern und Bindestriche; nicht mit Bindestrich beginnen oder enden."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-slate-400">
              Landet in Subdomain und Containernamen — der Firmenname taugt dafür nicht.
              Beim Anlegen einer Instanz wird sie vorgeschlagen und ist dort überschreibbar.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Rechnungs-E-Mail</label>
            <input
              required
              type="email"
              value={form.billingEmail}
              onChange={(e) => setForm((f) => ({ ...f, billingEmail: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Technische E-Mail (optional)
            </label>
            <input
              type="email"
              value={form.technicalEmail}
              onChange={(e) => setForm((f) => ({ ...f, technicalEmail: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as CustomerStatus }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="ACTIVE">Aktiv</option>
              <option value="SUSPENDED">Gesperrt</option>
              <option value="CANCELLED">{t('customers.terminated')}</option>
            </select>
          </div>
          <div className="pt-2 border-t border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Rechnungsdaten</h3>
            <p className="text-xs text-slate-500 mb-3">
              Name und vollständige Anschrift sind für eine ordnungsgemäße Rechnung nach §14 UStG
              erforderlich.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rechnungsempfänger
                </label>
                <input
                  value={form.billingName}
                  onChange={(e) => setForm((f) => ({ ...f, billingName: e.target.value }))}
                  placeholder={form.companyName || 'Abweichender Name (optional)'}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Straße und Hausnummer
                </label>
                <input
                  value={form.billingAddressLine1}
                  onChange={(e) => setForm((f) => ({ ...f, billingAddressLine1: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Adresszusatz (optional)
                </label>
                <input
                  value={form.billingAddressLine2}
                  onChange={(e) => setForm((f) => ({ ...f, billingAddressLine2: e.target.value }))}
                  placeholder={t('customers.addressLine2Example')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">PLZ</label>
                  <input
                    value={form.billingPostalCode}
                    onChange={(e) => setForm((f) => ({ ...f, billingPostalCode: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ort</label>
                  <input
                    value={form.billingCity}
                    onChange={(e) => setForm((f) => ({ ...f, billingCity: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Land</label>
                  <input
                    value={form.billingCountry}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, billingCountry: e.target.value.toUpperCase() }))
                    }
                    maxLength={2}
                    placeholder="DE"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Zahlungsziel (Tage)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={form.paymentTermsDays}
                    onChange={(e) => setForm((f) => ({ ...f, paymentTermsDays: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">USt-IdNr.</label>
                  <input
                    value={form.vatId}
                    onChange={(e) => setForm((f) => ({ ...f, vatId: e.target.value.toUpperCase() }))}
                    placeholder="DE123456789"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Steuernummer
                  </label>
                  <input
                    value={form.taxNumber}
                    onChange={(e) => setForm((f) => ({ ...f, taxNumber: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
              </div>

              {form.vatId && form.billingCountry && form.billingCountry !== 'DE' && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  USt-IdNr. mit Land ≠ DE — Rechnungen an diesen Kunden sind als Reverse-Charge zu
                  behandeln.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rechnungs-E-Mail (optional)
                </label>
                <input
                  type="email"
                  value={form.invoiceEmail}
                  onChange={(e) => setForm((f) => ({ ...f, invoiceEmail: e.target.value }))}
                  placeholder={form.billingEmail || 'Abweichende Adresse für Rechnungen'}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Bestellnummer / Kostenstelle (optional)
                </label>
                <input
                  value={form.customerReference}
                  onChange={(e) => setForm((f) => ({ ...f, customerReference: e.target.value }))}
                  placeholder={t('customers.shownOnInvoice')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            Zugänge zum Kundenbereich werden in der aufgeklappten Kundenzeile verwaltet.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60"
            >
              {mutation.isPending ? 'Wird gespeichert…' : customer ? 'Speichern' : 'Anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
